import { Fragment, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  Stack,
  FormControl,
  Typography,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Chip,
  Collapse,
  CircularProgress,
  Grid
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import {
  ConfirmDialog,
  NoDataAvailable
} from '../../components/_dashboard/app';
import { CustomPagination, SnackBarAlert } from '../../components/HelperComponents';
import { useAuth } from '../../components/customContextProviders/AuthContext';
import { useBranches } from '../hooks/useBranches';
import { DeviceOrderdButtons } from '../../components/_dashboard/soundbox';
import { APP_CONSTANTS } from '../../URLConstants';
import AddBranchModal from './AddBranchModal';
import BranchMoreMenu from './BranchMoreMenu';



export default function BranchContentCard() {
  const { sessionUserData, regionsList, zonesList, fetchZonesList, fetchRegionList, headOfficesList } = useAuth();
  const branchTableRef = useRef(null);
  const {
    page,
    searchText,
    setSearchText,
    searchBy,
    handleSearchByChange,
    branchesListData,
    setPage,
    isSnackBarAlertOpen,
    setIsSnackBarAlertOpen,
    snackBarMetas,

    handleChangePage,
    handleSearch,
    onUpdateBranchAction,
    handleBranchMoreMenuClick,
    branchModalOpen,
    setBranchModalOpen,
    selectedBranchForAction,
    setSelectedBranchForAction
  } = useBranches()

  const isAdmin = [APP_CONSTANTS?.USER_ROLE_ADMIN, APP_CONSTANTS?.USER_ROLE_SUPER_ADMIN]?.includes(
    sessionUserData?.user?.role
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2
        }}
      >
        {isAdmin && (
          <Button
            variant="contained"
            color="whitebg"
            size="small"
            sx={{ mr: 1, minHeight: '4vh', minWidth: 110 }}
            onClick={() => {
              setBranchModalOpen(true);
              setSelectedBranchForAction(null);
              fetchZonesList()
            }}
          >
            Add New Branch
          </Button>
        )}
      </Box>

      <Card>
        <Stack
          direction={{ md: 'row', xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ p: 2 }}
          mt={1}
          mb={1}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <FormControl size="small" sx={{ minWidth: 60 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, whiteSpace: 'nowrap' }}>
                Search by
              </Typography>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={searchBy}
                onChange={(event) => handleSearchByChange(event?.target?.value)}
              >
                <MenuItem value="name">Branch Name</MenuItem>
                <MenuItem value="branchId">Branch ID</MenuItem>
              </Select>
            </FormControl>

            <Typography sx={{ fontSize: 16, fontWeight: 700, whiteSpace: 'nowrap' }}>
              =
            </Typography>

            <TextField
              size="small"
              label={searchBy === 'branchId' ? 'Enter Branch ID' : 'Enter Branch Name'}
              value={searchText}
              onChange={(event) => setSearchText(event?.target?.value)}
              InputProps={{
                inputProps: {
                  'data-testid': 'searchText'
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => handleSearch(searchText)}
                      aria-label="Search branches"
                    >
                      <SearchOutlinedIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleSearch(event?.target?.value)
                }
              }}
            />
          </Stack>
        </Stack>

        <Divider sx={{ mx: 1 }} />

        {branchesListData?.data !== null && branchesListData?.data !== undefined ? (
          <>
            <TableContainer
              sx={{ maxHeight: '60vh', minHeight: '60vh', overflowY: 'scroll' }}
              ref={branchTableRef}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                    <TableCell sx={{ fontWeight: 900 }}>Branch Name</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Branch ID</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Region</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>District</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>City</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>State</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Pincode</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Address</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {branchesListData?.loading ? <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress size={40} />
                    </TableCell>
                  </TableRow> : null}
                  {branchesListData?.data?.map((row) => (
                    <Fragment key={row?.id}>
                      <TableRow
                        hover
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        tabIndex={-1}
                      >
                        <TableCell className="merchantTableListLabel" sx={{ textTransform: 'capitalize' }}>
                          {row?.name || '---'}
                        </TableCell>
                        <TableCell className="merchantTableListLabel" sx={{ textTransform: 'capitalize' }}>
                          {row?.branchId || '---'}
                        </TableCell>
                        <TableCell className="merchantTableListLabel" sx={{ textTransform: 'capitalize' }}>
                          {regionsList?.find((item) => item.id === row?.regionalOfficeId)?.name || '---'}
                        </TableCell>
                        <TableCell className="merchantTableListLabel">
                          {row?.district || '---'}
                        </TableCell>
                        <TableCell className="merchantTableListLabel">
                          {row?.city || '---'}
                        </TableCell>
                        <TableCell className="merchantTableListLabel">
                          {row?.state || '---'}
                        </TableCell>
                        <TableCell className="merchantTableListLabel">
                          {row?.pincode || '---'}
                        </TableCell>
                        <TableCell className="merchantTableListLabel">
                          <Typography className='merchantTableListLabel'>{row?.phone || '---'}</Typography>
                        </TableCell>


                        <TableCell className="merchantTableListLabel">
                          {row?.address || '---'}
                        </TableCell>
                        <TableCell className="merchantTableListLabel">
                          {isAdmin && (
                            <BranchMoreMenu
                              handleUser={row}
                              handleClicks={(action) =>
                                handleBranchMoreMenuClick(action, row)
                              }
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  ))}
                  {branchesListData?.data?.length === 0 && branchesListData?.data !== true && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <NoDataAvailable />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomPagination
              page={page}
              handleNextPage={() => handleChangePage('NEXT')}
              handlePreviousPage={handleChangePage}
              hasNextPage={branchesListData?.metas?.hasNextPage}
              data={branchesListData}
              setPage={setPage}
            />
          </>
        ) : null}
      </Card>

      <SnackBarAlert
        open={isSnackBarAlertOpen}
        title={snackBarMetas?.title}
        message={snackBarMetas?.message}
        severity={snackBarMetas?.severity}
        handleClose={() => setIsSnackBarAlertOpen(false)}
      />

      <AddBranchModal
        opneAssignedModal={branchModalOpen}
        handleClose={() => {
          setBranchModalOpen(false)
        }}
        zonesList={zonesList}
        regionsList={regionsList}
        headOfficesList={headOfficesList}
        fetchZones={fetchZonesList}
        fetchRegions={fetchRegionList}
        selectedBranch={selectedBranchForAction}
        onUpdate={(action, msg) => onUpdateBranchAction(action, msg)}
      />
    </>
  );
}
